<template>
    <app-layout title="Tickets Management">
        <template #header>
            <div class="flex justify-between">
                <h2 class="font-semibold text-2xl text-gray-800 dark:text-gray-200 leading-tight">
                    Tickets
                </h2>
            </div>
            <div class="flex mt-5">
                <label class="block w-1/3 text-sm mb-4 mr-4">
                    <span class="block font-medium text-sm text-gray-700 dark:text-gray-400">Select client</span>
                    <jet-select v-model:selected="selectedClient"
                                class="block w-full mt-2 text-sm dark:text-gray-300 dark:border-gray-600 dark:bg-gray-700 form-select focus:border-red-400 focus:outline-none focus:shadow-outline-red dark:focus:shadow-outline-gray">
                        <option :value="null">- All clients -</option>
                        <option v-for="(client, clientId) in clients" :key="'client-' + clientId" :value="clientId">{{
                                client
                            }}
                        </option>
                    </jet-select>

                </label>
                <label class="block w-1/3 text-sm mb-4 mr-4">
                    <span class="block font-medium text-sm text-gray-700 dark:text-gray-400">Select project</span>
                    <jet-select v-model:selected="selectedProject" :disabled="projects === null"
                                class="block w-full mt-2 text-sm dark:text-gray-300 dark:border-gray-600 dark:bg-gray-700 form-select focus:border-red-400 focus:outline-none focus:shadow-outline-red dark:focus:shadow-outline-gray">
                        <option :value="null">- All projects -</option>
                        <option v-for="(project, projectId) in projects" :key="'project-' + projectId"
                                :value="projectId">{{
                                project
                            }}
                        </option>
                    </jet-select>
                </label>

                <label class="block w-1/4 text-sm mb-4">
                    <span class="block font-medium text-sm text-gray-700 dark:text-gray-400">Select status</span>
                    <jet-select v-model:selected="selectedStatus"
                                class="block w-full mt-2 text-sm dark:text-gray-300 dark:border-gray-600 dark:bg-gray-700 form-select focus:border-red-400 focus:outline-none focus:shadow-outline-red dark:focus:shadow-outline-gray">
                        <option v-for="status in statuses" :key="'status-' + status"
                                :value="status">{{
                                status
                            }}
                        </option>
                    </jet-select>
                </label>
            </div>
        </template>
        <template v-if="paginatedTickets.data.length">
            <table-layout>
                <template v-slot:theadRow>
                    <th class="px-4 py-3">Name</th>
                    <th class="px-4 py-3">Status</th>
                    <th class="px-4 py-3">Priority</th>
                    <th class="px-4 py-3">Comments</th>
                    <th class="px-4 py-3">Created</th>
                    <th class="px-4 py-3">Updated</th>
                </template>

                <template v-slot:customContent>
                    <tr v-for="ticket in paginatedTickets.data" class="text-gray-700 dark:text-gray-400">
                        <td class="px-4 py-3">
                            <div class="flex items-center text-sm">
                                <div>
                                    <inertia-link
                                        class="inline-flex items-center w-full text-sm font-semibold transition-colors duration-150 hover:text-gray-800 dark:hover:text-gray-200"
                                        :href="route('admin.ticket.show', ticket.id)"
                                    >
                                        <p class="font-semibold">{{ ticket.title }}</p>
                                    </inertia-link>

                                </div>
                            </div>
                        </td>
                        <td class="px-4 py-3 text-sm">
                            {{ ticket.status }}
                        </td>
                        <td class="px-4 py-3 text-sm">
                            {{ ticket.priority }}
                        </td>
                        <td class="px-4 py-3 text-sm">
                            {{ ticket.comments_count }}
                        </td>
                        <td class="px-4 py-3 text-sm">
                            {{ dayjs(ticket.created_at).format("DD-MM-YYYY HH:mm") }}
                        </td>
                        <td class="px-4 py-3 text-sm">
                            {{ dayjs(ticket.updated_at).format("DD-MM-YYYY HH:mm") }}
                        </td>
                    </tr>
                </template>
            </table-layout>
            <table-pagination :pagination-object="paginatedTickets"/>
        </template>
        <template v-else>
            <h4 class="font-semibold text-gray-800 dark:text-gray-200 leading-tight">
                No {{ $page.props.selectedStatus }} tickets found.
            </h4>
        </template>
    </app-layout>
</template>

<script>
import {defineComponent, ref, watch} from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import TablePagination from "@/Jetstream/TablePagination";
import TableLayout from "@/Jetstream/TableLayout";
import JetSelect from "@/Jetstream/Select";
import Pill from "@/Jetstream/Pill";
import {Inertia} from '@inertiajs/inertia';
import dayjs from "dayjs";

export default defineComponent({
    methods: {dayjs},
    components: {
        Pill,
        TableLayout,
        TablePagination,
        AppLayout,
        JetSelect,
    },
    props: {
        paginatedTickets: {
            type: Object,
            required: true,
        },
        clients: {
            type: Object,
            required: true,
        },
        projects: {
            type: Object,
            required: true,
        },
        statuses: {
            type: Object,
            required: true,
        },
        selectedClient: {
            type: Number,
            default: null
        },
        selectedProject: {
            type: Number,
            default: null
        },
        selectedStatus: {
            type: String,
            default: null
        }
    },
    setup(props) {
        const selectedClient = ref(props.selectedClient);
        const selectedProject = ref(props.selectedProject);
        const selectedStatus = ref(props.selectedStatus);

        watch(selectedClient, () => {
            Inertia.visit(route('admin.ticket.index', {
                    ...(selectedClient.value && {client: selectedClient.value}),
                    ...(selectedStatus.value && {status: selectedStatus.value}),
                })
            );
        });

        watch([selectedProject, selectedStatus], () => {
            Inertia.visit(route('admin.ticket.index', {
                    ...(selectedProject.value && {project: selectedProject.value} || (selectedClient.value && {client: selectedClient.value})),
                    ...(selectedStatus.value && {status: selectedStatus.value}),
                })
            );
        });

        return {
            selectedClient,
            selectedProject,
            selectedStatus,
        };
    }
})
</script>
